import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';

const typeTabs = ['全部', '影视资源', '资讯'];

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState('全部');

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentTitle(query ? `“${query}”的搜索结果 - 蜜桃网` : '搜索 - 蜜桃网', {
    description: `在蜜桃网搜索“${query}”，查找相关影视资源与影视资讯。`,
    keywords: `蜜桃网,搜索,${query},影视搜索`,
  });

  const keyword = query.trim().toLowerCase();

  const matchedMovies = useMemo(() => {
    if (!keyword) return [];
    return movies.filter((m) =>
      [m.title, m.type, m.region, m.director, ...m.genres, ...m.actors]
        .join(' ')
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const matchedNews = useMemo(() => {
    if (!keyword) return [];
    return news.filter((a) =>
      [a.title, a.excerpt, a.category, a.author, ...a.content].join(' ').toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const showMovies = tab === '全部' || tab === '影视资源';
  const showNews = tab === '全部' || tab === '资讯';
  const total = (showMovies ? matchedMovies.length : 0) + (showNews ? matchedNews.length : 0);
  const hasKeyword = keyword.length > 0;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索</h1>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative w-full">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
            <input
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、类型、演员或资讯关键词"
              className="h-12 w-full rounded-full border border-background-200 bg-background-100 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
            />
          </div>
          <button
            type="submit"
            className="h-12 shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-8 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {hasKeyword && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="text-sm text-foreground-600">
              关键词「<span className="font-semibold text-primary-700">{query}</span>」共找到{' '}
              <span className="font-semibold text-accent-600">{total}</span> 条结果
            </p>
          </div>
        )}

        {hasKeyword && (
          <div className="mt-4 flex flex-wrap gap-2">
            {typeTabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {!hasKeyword ? (
        <div className="mx-auto mt-10 max-w-3xl">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="支持搜索影视资源与资讯文章，试试输入“科幻”“悬疑”或片名。"
          />
        </div>
      ) : total === 0 ? (
        <div className="mx-auto mt-10 max-w-3xl">
          <EmptyState
            title="没有找到相关结果"
            description="换个关键词试试，或者浏览下面的热门推荐。"
          />
          <div className="mt-8">
            <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">热门推荐</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5" data-product-shop>
              {movies.slice(0, 3).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          {showMovies && matchedMovies.length > 0 && (
            <section data-product-shop>
              <h2 className="mb-4 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500" />
                影视资源（{matchedMovies.length}）
              </h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {matchedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}

          {showNews && matchedNews.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-4 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500" />
                资讯（{matchedNews.length}）
              </h2>
              <div className="flex flex-col gap-4">
                {matchedNews.map((a) => (
                  <Link
                    key={a.id}
                    to={`/news/detail/${a.id}`}
                    className="group flex gap-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-50 p-3 transition-all duration-300 hover:border-primary-300 hover:bg-background-100"
                  >
                    <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={a.cover}
                        alt={a.title}
                        title={`${a.title} - 蜜桃网影视资讯`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-xs font-medium text-accent-600">{a.category}</span>
                      <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                        {a.title}
                      </h3>
                      <span className="mt-auto pt-2 text-xs text-foreground-400">
                        {a.author} · {a.date}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}